import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level boundary. Failures are contained to a branded screen rather than a
 * blank page, and no user data is leaked into the UI. Errors would be forwarded
 * to the observability pipeline in a deployed environment.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[agave] render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <div className="panel max-w-md p-8 text-center">
          <div className="mx-auto grid size-14 place-items-center rounded-2xl bg-crimson-500/15">
            <AlertOctagon className="size-7 text-crimson-500" />
          </div>
          <h1 className="mt-5 font-display text-2xl font-bold text-white">Something went wrong</h1>
          <p className="mt-2 text-sm text-ink-300">
            The table hit a snag. Your balance and history are safe. Reload to continue playing.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-agave-400 to-agave-600 px-5 py-3 font-semibold text-ink-1000"
          >
            <RefreshCw className="size-4" /> Reload
          </button>
          <p className="mt-4 font-mono text-[11px] text-ink-600">
            {this.state.error.name}: {this.state.error.message}
          </p>
        </div>
      </div>
    );
  }
}
